/* eslint-disable no-console */
import { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { TableSearch } from "../../../components/Table/TableActions";
import { toast, ToastContainer } from "react-toastify";
import Badge from "../../../components/Badge/Badge";
import useWindowSize from "../../../hooks/useWindow";
import Table from "../../../components/Table/Table";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button/Button";
import { httprequest } from "../../../data/api";
import InputGroup from "../../../components/FormComponent/InputGroup";
import Select from "react-select";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import ReactQuill from "react-quill";
import { Grid } from "@mui/material";

const NOTIFICATION_ENDPOINT = "/admin/v1/system-notifications";

const CreatedNotification = () => {
  const [notifications, setNotifications] = useState([]);
  const [searchResult, setSearchResult] = useState([]);
  const width = useWindowSize();
  const mobile = width < 600;
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [editData, setEditData] = useState({
    type: "",
    title: "",
    message: "",
    start_time: "",
    end_time: "",
    location: []
  });

  const getNotifications = () => {
    httprequest
      .get(NOTIFICATION_ENDPOINT)
      .then((res) => {
        const fetchedNotifications = res?.data?.data || [];
        setNotifications(fetchedNotifications);
        setSearchResult(fetchedNotifications);
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  useEffect(() => {
    getNotifications();
  }, []);

  const locations = [
    { value: "dashboard", label: "Dashboard" },
    { value: "payment", label: "Payment" },
    { value: "enrollment", label: "Enrollment" },
    { value: "integration", label: "Integration" }
  ];

  const handleSearchNotification = (e) => {
    const { value } = e.target;
    const result = notifications.filter((notification) => notification.title.toLowerCase().includes(value.toLowerCase()) ||
      notification.type.toLowerCase().includes(value.toLowerCase()) ||
      notification.location.toLowerCase().includes(value.toLowerCase()) ||
      notification.status.toLowerCase().includes(value.toLowerCase())
    );
    setSearchResult(result);
  };

  const handleEdit = (id) => {
    const notificationToEdit = notifications.find(notification => notification.id === id);
    if (notificationToEdit) {
      setEditData({
        ...notificationToEdit,
        start_time: notificationToEdit.start_time?.substring(0, 10) || "",
        end_time: notificationToEdit.end_time?.substring(0, 10) || "",
        location: notificationToEdit.location || []
      });
      setEditModal(true);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
  };

  const handleDeleteModal = (row) => {
    if (row?.id) {
      setCurrentRow(row);  // Ensure the row has an id
      setDeleteModal(!deleteModal);
    } else {
      toast.error("Notification ID is missing.");
    }
  };

  const handleCloseEditModal = () => {
    setEditModal(false);
    setEditData({
      type: "",
      title: "",
      message: "",
      start_time: "",
      end_time: "",
      location: []
    });
  };

  const handleEditSubmit = () => {
    const updatedData = {
      ...editData,
      location: Array.isArray(editData.location) ? editData.location.map(loc => loc.value) : []
    };

    httprequest.patch(`${NOTIFICATION_ENDPOINT}/${editData.id}`, updatedData)
      .then((res) => {
        toast.success(res.data.message);
        handleCloseEditModal();
        getNotifications();
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  const handleDeleteSubmit = () => {
    if (!currentRow.id) {
      toast.error("Notification ID is missing.");

      return;
    }

    httprequest.delete(`${NOTIFICATION_ENDPOINT}/${currentRow?.id}`)
      .then((res) => {
        toast.success(res.data.message);
        handleCloseDeleteModal();
        getNotifications();
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  const handleLocationChange = (selectedOptions) => {
    setEditData(prevState => ({
      ...prevState,
      location: selectedOptions
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const columns = [
    {
      name: "Type",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Type</p>}
          {row.type || "--/--"}
        </>
      )
    },
    {
      name: "Title",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Title</p>}
          {row.title || "--/--"}
        </>
      )
    },
    {
      name: "Location",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Location</p>}
          {row.location || "--/--"}
        </>
      )
    },
    {
      name: "Status",
      center: true,
      cell: (row) => <Badge status={row.status} />
    },
    {
      name: "Starts On",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Starts On</p>}
          {row.start_time?.substring(0, 10) || "--/--"}
        </>
      )
    },
    {
      name: "Ends On",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Ends On</p>}
          {row.end_time?.substring(0, 10) || "--/--"}
        </>
      )
    },
    {
      center: true,
      sortable: false,
      cell: (row) => (
        <div className="table-action-container">
          <button className="table-action-icon" onClick={() => handleEdit(row.id)}>
            <Icon icon="heroicons:pencil-square-solid" />
          </button>
          <button className="table-action-icon delete-icon" onClick={() => handleDeleteModal(row)}>
            <Icon icon="mingcute:delete-2-line" />
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <div className="page-content">
        <Table
          data={searchResult}
          columns={columns}
          subHeader={true}
          subHeaderComponent={
            <TableSearch
              placeholder="Search by title, type, or location..."
              searchTableFunc={handleSearchNotification}
            />
          }
          pagination
        />
      </div>
      <ToastContainer />
      {/* Delete Modal */}
      <Modal
        title="Delete Notification"
        subtitle={
          <div>Are you sure you want to delete selected {currentRow?.title}</div>
        }
        modalContent={<></>}
        modalFooter={
          <>
            <Button variant="danger" buttonName="Delete Notification" buttonSize="full" color="btndefault" onClick={handleDeleteSubmit} />
          </>
        }
        isopen={deleteModal}
        closeModal={handleCloseDeleteModal}
      />
      <Modal
        title="Edit Notification"
        subtitle={<div>Edit the notification details below.</div>}
        modalContent={
          <>
            <SelectGroup
              label="Type"
              required
              children={
                <select
                  name="type"
                  value={editData.type || ""}
                  onChange={handleInputChange}
                >
                  <option value="">--- Select Type---</option>
                  <option value="Information">Information</option>
                  <option value="Warning">Warning</option>
                  <option value="Danger">Danger</option>
                </select>
              }
            />
            <InputGroup
              label="Title"
              margin="normal"
              placeHolder="Title"
              inputType="text"
              name="title"
              value={editData.title || ""}
              onChange={handleInputChange}
            />
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <p style={{ marginBottom: 8 }} className="input-picker-title">
                Select Location(s)<span className="input-required">*</span>
              </p>
              <Select
                isMulti
                options={locations}
                placeholder="Select Location(s)"
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 })
                }}
                value={editData.location}
                onChange={handleLocationChange}
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <p style={{ fontWeight: 500, fontSize: "14px", marginBottom: 8, marginTop: 10 }} className="text-align-left no-margin-block">
                Notification Message
                <span className="input-required">*</span>
              </p>
              <ReactQuill
                theme="snow"
                value={editData.message || ""}
                onChange={(content) => setEditData(prevState => ({
                  ...prevState,
                  message: content
                }))}
                className="quill-editors"
                style={{
                  borderRadius: 30,
                  textAlign: "left"
                }}
              />
            </Grid>
            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Starts on:"
                margin="normal"
                placeHolder="Starts On"
                inputType="date"
                inputMin={new Date().toISOString().split("T")[0]}
                name="start_time"
                value={editData.start_time || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item lg={6} md={6} sm={12} xs={12}>
              <InputGroup
                label="Ends on:"
                margin="normal"
                placeHolder="Ends On"
                inputType="date"
                inputMin={new Date().toISOString().split("T")[0]}
                name="end_time"
                value={editData.end_time || ""}
                onChange={handleInputChange}
              />
            </Grid>
          </>
        }
        modalFooter={
          <>
            <Button variant="primary" buttonName="Save Changes" buttonSize="full" color="btndefault" onClick={handleEditSubmit} />
          </>
        }
        isopen={editModal}
        closeModal={handleCloseEditModal}
      />
    </>
  );
};

export default CreatedNotification;
